import axios, { AxiosRequestConfig } from 'axios';

import {
  ISaltclickConversationListResponse,
  ISaltclickScrt2AccessTokenResponse,
} from '../types/SaltclickChat';

/**
 * Salesforce's Messaging for In-App and Web (MIAW) REST host, reached on bare `axios`
 * rather than through `tempMeeApi`: it is not our API, so nothing that instance does
 * applies — no `baseURL`, no request interceptor putting a portal access token and an
 * `x-office` header on a Salesforce request, and no cookies.
 *
 * Reaching it from the portal's own origin works because scrt2 answers
 * `access-control-allow-origin: *` and allows an `authorization` header on the preflight —
 * both measured, not assumed. These calls are the same hops the widget makes inside its
 * iframe; the point of making them out here is that the iframe's traffic is invisible to
 * this document, so what the widget believes about a conversation and what Salesforce
 * actually holds can only be compared from outside it.
 */
/**
 * How long any one scrt2 call gets. Salesforce is a third party with no stake in our
 * page staying responsive, and the user is waiting on this: the launcher is inert while
 * a conversation is being retired, so a call that hangs is a launcher that hangs.
 */
const SCRT2_TIMEOUT_MS = 8000;

const scrt2Config = (accessToken?: string): AxiosRequestConfig => ({
  timeout: SCRT2_TIMEOUT_MS,
  // Axios omits cookies by default; said out loud because `tempMeeApi` does the opposite,
  // and because a credentialed request to a host answering `*` fails outright.
  withCredentials: false,
  headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
});

/**
 * The API capabilities version declared on the access-token exchange. Not optional:
 * without it the endpoint answers
 * `400 object has missing required properties (["capabilitiesVersion"])`.
 *
 * `262` is what this deployment's own client sends — a constant in its bundle. Expect
 * to bump it when Salesforce does; the symptom will be a 400 naming a capability
 * rather than a missing property.
 */
const CAPABILITIES_VERSION = '262';

const ACCESS_TOKEN_PATH = '/iamessage/v1/authorization/authenticated/accessToken?clientName=Web_v2';
const CONVERSATION_LIST_PATH = '/iamessage/v1/queries/conversation/list';
const CONVERSATION_PATH = '/iamessage/v1/conversation';

interface IScrt2Request {
  /** MIAW host for the environment, from `SALTCLICK_CHAT_CONFIG`. */
  scrt2Url: string;
}

interface IScrt2AuthorizedRequest extends IScrt2Request {
  /** From `fetchSaltclickScrt2AccessToken`. Not the portal's access token. */
  accessToken: string;
}

export interface IFetchSaltclickScrt2AccessTokenArgs extends IScrt2Request {
  orgId: string;
  /** Embedded Service deployment name (`esConfigName`). */
  deploymentName: string;
  /**
   * A freshly minted identity JWT. Minted for this call rather than borrowed from the
   * widget: an access-token exchange creates no conversation, so a token of our own
   * cannot disturb a chat in progress.
   */
  identityToken: string;
}

/**
 * Trades an identity JWT for a scrt2 access token — the first hop the widget makes,
 * made from this document instead. Every other call here needs the result.
 */
export const fetchSaltclickScrt2AccessToken = async ({
  scrt2Url,
  orgId,
  deploymentName,
  identityToken,
}: IFetchSaltclickScrt2AccessTokenArgs): Promise<string> => {
  const { data } = await axios.post<ISaltclickScrt2AccessTokenResponse>(
    `${scrt2Url}${ACCESS_TOKEN_PATH}`,
    {
      orgId,
      developerName: deploymentName,
      authorizationType: 'JWT',
      customerIdentityToken: identityToken,
      capabilitiesVersion: CAPABILITIES_VERSION,
    },
    scrt2Config(),
  );

  if (!data?.accessToken) {
    throw new Error('[Saltclick] the scrt2 access-token exchange returned no accessToken');
  }

  return data.accessToken;
};

/**
 * What Salesforce still holds open for this account.
 *
 * `includeClosedConversations: false`, so anything that comes back is still open — and
 * an open conversation is one the widget will resume on its next boot, which is the
 * carry-over a chat that ended is supposed to leave behind. It is also the authority on
 * what may be closed: deleting an id this does not list answers `403 Permission
 * Denied`, the same reply as a conversation belonging to someone else.
 */
export const fetchOpenSaltclickConversationIds = async ({
  scrt2Url,
  accessToken,
}: IScrt2AuthorizedRequest): Promise<string[]> => {
  const { data } = await axios.post<ISaltclickConversationListResponse>(
    `${scrt2Url}${CONVERSATION_LIST_PATH}`,
    { includeClosedConversations: false },
    scrt2Config(accessToken),
  );

  return (data?.conversations ?? [])
    .map((conversation) => conversation.conversationId ?? conversation.id)
    .filter((conversationId): conversationId is string => !!conversationId);
};

export interface ICloseSaltclickConversationArgs extends IScrt2AuthorizedRequest {
  conversationId: string;
}

/**
 * Retires a conversation for good — the widget's own `closeConversation`, which nothing
 * on the bootstrap surface calls. This is the lever the client does not have, and the
 * only thing that makes the next chat a new conversation rather than a resumption of
 * this one.
 */
export const closeSaltclickConversation = async ({
  scrt2Url,
  accessToken,
  conversationId,
}: ICloseSaltclickConversationArgs): Promise<void> => {
  // No query string, matching the widget's own `closeConversation` exactly — it is
  // `endSession` that appends `?esDeveloperName=`, and a parameter this endpoint does
  // not expect is a 400 waiting to happen.
  await axios.delete(`${scrt2Url}${CONVERSATION_PATH}/${conversationId}`, scrt2Config(accessToken));
};
